import React, { DOMAttributes, FC, useEffect, useRef, useState, useCallback } from "react";
import { Callout } from "../../notify";
import { Code } from "../../typography";
import { useEventListener } from "../../../utils/hooks";

type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;
type FieldValues = {
    [key: string]: null | string | number | boolean;
};

declare global {
    namespace JSX {
        interface IntrinsicElements {
            ["tv2-bits"]: CustomElement<any>;
        }
    }
}

export const BitsModuleView: FC<{
    useKey?: string;
    moduleSetKey?: string;
    moduleEntryKey?: string;
    version?: string;
    fields?: FieldValues;
    editable?: boolean;
    onChange?: (changes: FieldValues) => void;
}> = ({ useKey, moduleSetKey, moduleEntryKey, version, fields, editable, onChange }) => {
    const reloadRef = useRef<ReturnType<typeof setTimeout> | string | number>(0);
    const [currentNode, setCurrentNode] = useState<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState<boolean>(false);
    const handleSettingsChange = (event: Event) => {
        const { detail } = event as CustomEvent;
        onChange && onChange(detail);
    };
    useEventListener("change", handleSettingsChange, currentNode);

    useEffect(() => {
        setVisible(false);
        clearTimeout(reloadRef.current);
        reloadRef.current = setTimeout(() => {
            setVisible(true);
        }, 50);
        return () => {
            clearTimeout(reloadRef.current);
        };
    }, [useKey, moduleSetKey, moduleEntryKey, version]);

    const handleRef = useCallback(
        (node: HTMLDivElement) => {
            setCurrentNode(editable ? node : null);
        },
        [editable]
    );

    const formatFieldsForWebComponent = (fields: FieldValues | undefined) => {
        if (!fields) return {};
        // Allow truthy values, booleans (including false) and numbers (including 0), removes empty strings and null values
        const formattedDataSet = Object.entries(fields).reduce((acc, [key, value]) => {
            return value || typeof value === "boolean" || typeof value === "number" ? { ...acc, [key]: value } : acc;
        }, {} as FieldValues);
        return formattedDataSet;
    };

    return !visible ? null : window.hasOwnProperty("tv2bits") ? (
        React.createElement("tv2-bits", {
            ref: handleRef,
            use: useKey || `${moduleSetKey}/${moduleEntryKey}`,
            version,
            ...(editable && { editable: "true" }),
            ...formatFieldsForWebComponent(fields)
        })
    ) : (
        <Callout color="error">
            Bits tag is missing: <Code>{`<tv2-bits />`}</Code>
        </Callout>
    );
};
